import { IUpdateTagGroup, TagGroup } from 'contensis-management-api';
import mapErrorToObject from './mapAppErrorToObject';
import { MigrateStatus } from '~/models';
import ContensisRepository from '~/repositories/ContensisRepository';
import { compareFields } from '~/util/EntryHelper';

/**
 * Build a complete tag group specifically for the target repository, providing
 * context of source and target repositories we can validate any field in the
 * given source tag for any required dependencies
 *
 * Output the tag as JSON for loading into the Management API using the `toJSON` method
 */
export class TagGroupBuilder {
  contensis: ContensisRepository;
  origin: ContensisRepository;
  finalValue?: IUpdateTagGroup;

  existing?: TagGroup;
  source: TagGroup;
  status!: MigrateStatus;
  error?: MappedError;
  diff?: string;

  get id() {
    return this.source.id;
  }
  get friendlyName() {
    return this.source.name || this.existing?.name || this.id;
  }

  constructor(
    contensis: ContensisRepository,
    origin: ContensisRepository,
    source: TagGroup
  ) {
    this.contensis = contensis;
    this.origin = origin;
    this.source = source;
    this.init();
  }
  init = () => {
    const { contensis } = this;
    // Find existing tag group in target context
    // We may have set existing from outside
    if (!this.existing) {
      this.existing = contensis.context.tagGroups.find((g) => g.id === this.id);
    }
  };

  /**
   * @returns tag group as JSON
   */
  toJSON = (opts = {}) =>
    ({
      ...(this.existing || {}),
      ...(this.finalValue || {}),
    }) as IUpdateTagGroup;

  /**
   * Check we have the tag group and languages available to process this tag
   */
  validate = () => {
    // Removed name validation so we can create groups simply from the cli when we create just the tags with a groupId
    // Instead use "friendlyName" which falls back to use id when a name is not provided
    // if (typeof this.source.name !== 'string') {
    //   this.error = mapErrorToObject(
    //     new Error(`Tag group '${this.friendlyName}' requires a valid name`)
    //   );
    // }
    if (typeof this.id !== 'string') {
      this.error = mapErrorToObject(
        new Error(`Tag group '${this.friendlyName}' requires a valid id`)
      );
    }
  };

  /**
   * Build the tag group for the target taking the values from the source tag group
   */
  build = () => {
    try {
      // Build the tag group and set the migrate status
      this.validate();
      this.finalValue = {
        id: this.id,
        name: this.friendlyName,
        description: this.source.description,
      };

      this.status = this.checkStatus();
    } catch (ex: any) {
      this.error = mapErrorToObject(ex);
      this.error.message = `Error building tag group ${this.id}: ${this.error.message}`;
    }
  };

  /**
   * Check the tag group for update status
   */
  checkStatus = (): MigrateStatus => {
    const { contensis, error, existing, nullFields } = this;

    if (error) return 'error';

    if (existing) {
      // Has the tag group changed from the existing?
      const tagGroup = this.toJSON();

      const { changed, diff } = compareFields(
        nullFields(tagGroup) as TagGroup,
        nullFields(existing) as TagGroup,
        this.origin,
        contensis
      );

      if (changed) {
        this.diff = diff;
        return 'update';
      } else {
        return 'no change';
      }
    }

    return 'create';
  };
  /**
   * Set Contensis controlled fields to null before doing a diff
   * as these will always be different */
  nullFields = (group: IUpdateTagGroup) =>
    ({
      ...group,
      tagCount: undefined,
      version: undefined,
    }) as IUpdateTagGroup;
}
